/* [EZ] Hero — crossfade slideshow + 3-label center-active nav.
   The active label's vertical progress line IS the clock: its CSS fill animation
   runs for `autoplay` seconds, and its `animationend` advances the slide. Pause on
   hover freezes the fill (so the clock pauses too). Reduced motion = static. */
(function () {
  'use strict';

  class EZHero extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.labels = this.slides.map((s) => s.dataset.label || '');
      this.n = this.slides.length;
      this.navStyle = this.dataset.navStyle === 'line' ? 'line' : 'default';
      this.track = this.querySelector('[data-nav-track]');
      this.barsWrap = this.querySelector('[data-bars]');
      this.navWrap = this.querySelector('.ez-hero__nav-wrap');
      this.fill = this.querySelector('.ez-hero__nav-line .ez-hero__nav-fill');
      this.index = 0;
      this.activeRow = null;

      const secs = parseInt(this.dataset.autoplay, 10) || 5;
      this.style.setProperty('--ez-hero-dur', secs + 's');

      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplayDisabled = reduce || this.n <= 1;

      if (this.n === 0) return;
      this.showSlide(0);

      if (this.n <= 1) {
        if (this.navWrap) this.navWrap.hidden = true;
        return;
      }

      if (this.navStyle === 'line') this.buildLineNav();
      else this.buildNav();
      this.addEventListener('mouseenter', () => this.classList.add('is-paused'));
      this.addEventListener('mouseleave', () => this.classList.remove('is-paused'));
      this.enableSwipe();
    }

    /* Horizontal swipe (mobile) → prev/next slide. Vertical scroll passes through
       (touch-action: pan-y in CSS). A real swipe suppresses the slide link's click. */
    enableSwipe() {
      const self = this;
      let x0 = 0, y0 = 0, on = false, dx = 0;
      this.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        on = true; dx = 0; x0 = e.clientX; y0 = e.clientY; self._swiped = false;
      });
      this.addEventListener('pointermove', (e) => {
        if (!on) return;
        const mx = e.clientX - x0, my = e.clientY - y0;
        if (Math.abs(mx) > Math.abs(my)) dx = mx;
      });
      const end = () => {
        if (!on) return; on = false;
        if (Math.abs(dx) > 40) { self._swiped = true; self.goSwipe(dx < 0 ? 1 : -1); }
      };
      this.addEventListener('pointerup', end);
      this.addEventListener('pointercancel', () => { on = false; });
      this.addEventListener('click', (e) => {
        if (self._swiped) { e.preventDefault(); e.stopPropagation(); self._swiped = false; }
      }, true);
    }

    goSwipe(dir) {
      const target = (this.index + dir + this.n) % this.n;
      if (this.navStyle === 'line') this.jumpToLine(target);
      else this.jumpTo(target);
    }

    /* ---- Line nav: a static row of bars; the active bar's L→R fill is the clock ---- */
    buildLineNav() {
      if (!this.barsWrap) return;
      this.barsWrap.innerHTML = '';
      this.bars = [];
      for (let k = 0; k < this.n; k++) {
        const bar = document.createElement('button');
        bar.type = 'button';
        bar.className = 'ez-hero__bar';
        bar.dataset.index = k;
        bar.setAttribute('aria-label', this.labels[k] || 'Slide ' + (k + 1));
        const f = document.createElement('i');
        f.className = 'ez-hero__bar-fill';
        bar.appendChild(f);
        bar.addEventListener('click', () => this.jumpToLine(k));
        this.barsWrap.appendChild(bar);
        this.bars.push(bar);
      }
      this.activateBar(this.index);
    }

    activateBar(i) {
      this.bars.forEach((b, k) => b.classList.toggle('is-active', k === i));
      // Detach the previous fill's listener from the element it actually sits on
      // (so a mid-fill jump doesn't leave a stale animationend listener behind).
      if (this._activeFill && this._onBarEnd) this._activeFill.removeEventListener('animationend', this._onBarEnd);
      this._activeFill = null;
      if (this.autoplayDisabled) return;
      const fill = this.bars[i] && this.bars[i].querySelector('.ez-hero__bar-fill');
      if (!fill) return;
      // Restart the active bar's fill from 0; its animationend advances the slide.
      fill.style.animation = 'none';
      void fill.offsetWidth;
      fill.style.animation = '';
      this._onBarEnd = () => { fill.removeEventListener('animationend', this._onBarEnd); this.advanceLine(); };
      this._activeFill = fill;
      fill.addEventListener('animationend', this._onBarEnd);
    }

    advanceLine() {
      this.index = (this.index + 1) % this.n;
      this.showSlide(this.index);
      this.activateBar(this.index);
    }

    jumpToLine(k) {
      if (k === this.index) return;
      this.index = k;
      this.showSlide(k);
      this.activateBar(k);
    }

    makeRow(k) {
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'ez-hero__nav-row';
      row.dataset.index = k;
      const prog = document.createElement('span');
      prog.className = 'ez-hero__nav-progress'; // transparent spacer — keeps labels aligned with the fixed line
      const lbl = document.createElement('span');
      lbl.className = 'ez-hero__nav-label';
      lbl.textContent = this.labels[k];
      row.appendChild(prog);
      row.appendChild(lbl);
      row.addEventListener('click', () => this.jumpTo(k));
      return row;
    }

    buildNav() {
      const i = this.index, n = this.n;
      const idxs = [(i - 1 + n) % n, i, (i + 1) % n];
      this.track.innerHTML = '';
      this.rows = idxs.map((k) => this.makeRow(k));
      this.rows.forEach((r) => this.track.appendChild(r));
      this.track.style.transition = 'none';
      this.track.style.transform = 'translateY(0)';
      void this.track.offsetWidth;
      this.track.style.transition = '';
      this.activate(this.rows[1]); // center row is active
    }

    activate(row) {
      if (this.activeRow) this.activeRow.classList.remove('is-active');
      this.activeRow = row;
      row.classList.add('is-active');
      if (this.autoplayDisabled || !this.fill) return;
      // Restart the FIXED progress line from 0 (it stays in place; only resets).
      const fill = this.fill;
      if (this._onEnd) fill.removeEventListener('animationend', this._onEnd);
      fill.style.animation = 'none';
      void fill.offsetWidth;
      fill.style.animation = '';
      this._onEnd = () => { fill.removeEventListener('animationend', this._onEnd); this.advance(); };
      fill.addEventListener('animationend', this._onEnd);
    }

    advance() {
      const n = this.n;
      const incoming = this.makeRow((this.index + 2) % n);
      this.track.appendChild(incoming); // 4 rows; the 4th sits below the window
      const rowH = this.rows[0].offsetHeight;
      const done = () => {
        this.track.removeEventListener('transitionend', done);
        this.track.style.transition = 'none';
        if (this.track.firstElementChild) this.track.firstElementChild.remove();
        this.track.style.transform = 'translateY(0)';
        void this.track.offsetWidth;
        this.track.style.transition = '';
        this.index = (this.index + 1) % n;
        this.rows = Array.from(this.track.children);
        this.activate(this.rows[1]);
        this.showSlide(this.index);
      };
      this.track.addEventListener('transitionend', done);
      this.track.style.transition = 'transform 0.5s ease';
      this.track.style.transform = 'translateY(-' + rowH + 'px)';
    }

    jumpTo(k) {
      if (k === this.index) return;
      this.index = k;
      this.buildNav();
      this.showSlide(k);
    }

    showSlide(i) {
      this.slides.forEach((sl, k) => sl.classList.toggle('is-active', k === i));
      this.notifyLogo(i);
    }

    // Tell the (transparent) header which logo this slide wants. ez-header.js listens.
    notifyLogo(i) {
      const sl = this.slides[i];
      const variant = sl && sl.dataset.headerLogo ? sl.dataset.headerLogo : 'light';
      document.dispatchEvent(new CustomEvent('ez:hero:logo', { detail: { variant: variant } }));
    }
  }

  function init(scope) {
    (scope || document).querySelectorAll('ez-hero').forEach(function (el) {
      if (!el.__ezHeroInit && el.isConnected) {
        // custom element upgrade handles connectedCallback; this is a fallback
      }
    });
  }

  if (!customElements.get('ez-hero')) {
    customElements.define('ez-hero', EZHero);
  }
  document.addEventListener('shopify:section:load', function (e) {
    // re-upgrade if needed (editor)
    init(e.target);
  });
})();
